import { ref, watch, type Ref, getCurrentScope, onScopeDispose, isRef, type WatchStopHandle } from "vue";
import { ftsSearch } from "./ftsSearch";
import { ftsSearchApi, shouldUseApiFts } from "./ftsSearchApi";
import { getContentPublishDateCutoff } from "../config";
import { isConnected } from "../socket/socketio";
import { OPEN_MIN } from "../api/sync/utils";
import type { FtsSearchOptions, FtsSearchResult, FtsSort } from "./types";
import { DocType, type ContentDto, type PublishStatus } from "../types";
import { attachFtsLiveSync, markFtsStale } from "./ftsLiveSync";

/**
 * Reactive non-language filters forwarded to each search. Changing the ref triggers a
 * fresh search (same as changing `languageId`). All fields are optional and only narrow
 * when present. See {@link FtsSearchOptions}.
 */
export type FtsFilterOptions = {
    types?: DocType[];
    tags?: string[];
    status?: PublishStatus;
    publishedAfter?: number;
    publishedBefore?: number;
    expiresAfter?: number;
    expiresBefore?: number;
    /** Strict mode: require every query word (≥3 chars) as a substring of title/author. */
    matchAllWords?: boolean;
    /** Strict mode: order by this field/direction instead of relevance. */
    sort?: FtsSort;
};

export type UseFtsSearchOptions = {
    languageId?: Ref<string | undefined>;
    /** Reactive extra filters (type/tag/status/publishDate). A change re-runs the search. */
    filters?: Ref<FtsFilterOptions | undefined>;
    /** Debounce delay in ms before running search. Use 0 or 'manual' to only search when runSearch() is called. */
    debounceMs?: number | "manual" | Ref<number | "manual">;
    pageSize?: number;
    maxTrigramDocPercent?: number;
    /** Strict substring heuristic for stale detection; false = fuzzy/related content mode. */
    strictMatch?: Ref<boolean> | (() => boolean);
};

export type UseFtsSearchReturn = {
    results: Ref<FtsSearchResult[]>;
    isSearching: Ref<boolean>;
    loadMore: () => Promise<void>;
    hasMore: Ref<boolean>;
    totalLoaded: Ref<number>;
    lastSearchedQuery: Ref<string>;
    /** Run a search immediately (useful for manual mode and forcing refresh on reopen). */
    runSearch: () => void;
    /** Invalidate any in-flight search and cancel a pending debounced run. Does not clear results. */
    cancel: () => void;
    /** Cancel in-flight work and clear all search state (results, pagination, last query). */
    reset: () => void;
    /** Where the current results came from: "local" (offline index) or "api" (server-side /fts). */
    source: Ref<"local" | "api">;
    /**
     * True when the showing results are an incomplete (recent-only) view: local search while a
     * `publishDate` sync cutoff is in effect — i.e. offline with selective sync, or an API-failure
     * fallback. False when full sync, or when complete API results are shown. UI can surface this
     * (e.g. "showing offline results — connect for full search").
     */
    isPartial: Ref<boolean>;
    isStale: Ref<boolean>;
    refresh: () => Promise<void>;
    markStale: () => void;
};

/**
 * Vue composable for full-text search with debouncing and infinite scroll support.
 * Search is fully async and non-blocking — each IndexedDB query yields to the event loop.
 */
export function useFtsSearch(
    queryRef: Ref<string>,
    options: UseFtsSearchOptions = {},
): UseFtsSearchReturn {
    const { debounceMs = 300, pageSize = 20, maxTrigramDocPercent } = options;
    const getDebounce = () => (isRef(debounceMs) ? debounceMs.value : debounceMs);
    const isTriggerOnly = () => {
        const d = getDebounce();
        return d === "manual" || d === 0;
    };

    const results = ref<FtsSearchResult[]>([]);
    const isSearching = ref(false);
    const totalLoaded = ref(0);
    const hasMore = ref(false);
    const source = ref<"local" | "api">("local");
    const isPartial = ref(false);
    const isStale = ref(false);
    /** When using triggerOnly, this is the query last passed to doSearch (so UI can show "no results" vs "press Go"). */
    const lastSearchedQuery = ref("");

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let currentQuery = "";
    let searchGeneration = 0;
    /** Route chosen at the start of the current search (offset 0); reused by loadMore so pages don't split across sources. */
    let activeUseApi = false;
    /** Query text of the fresh (offset-0) search currently in flight, if any — lets runSearch()
     *  dedupe against a same-query search already started by the debounce or a prior runSearch. */
    let inFlightFreshQuery: string | null = null;

    const cutoffSet = () => getContentPublishDateCutoff() !== OPEN_MIN;

    /**
     * Run one page against the chosen engine. On the initial search (offset 0) an API failure
     * falls back to local results; on load-more pages it does not (to avoid mixing sources within
     * one result list) and instead yields an empty page.
     */
    async function runEngine(
        query: string,
        offset: number,
        useApi: boolean,
        allowFallback: boolean,
    ): Promise<{ results: FtsSearchResult[]; usedLocal: boolean }> {
        const opts: FtsSearchOptions = {
            query,
            languageId: options.languageId?.value,
            ...(options.filters?.value ?? {}),
            limit: pageSize,
            offset,
            maxTrigramDocPercent,
        };
        if (useApi) {
            try {
                return { results: await ftsSearchApi(opts), usedLocal: false };
            } catch (e) {
                if (!allowFallback) {
                    // load-more API failure: stop paginating rather than mix in local results
                    console.warn("FTS API load-more failed:", e);
                    return { results: [], usedLocal: false };
                }
                console.warn("FTS API search failed, falling back to local:", e);
                return { results: await ftsSearch(opts), usedLocal: true };
            }
        }
        return { results: await ftsSearch(opts), usedLocal: true };
    }

    async function doSearch(query: string, offset: number, append: boolean) {
        if (!query || query.length < 3) {
            // Always clear on an empty/short query — even when paginating, so loadMore cannot
            // resurrect stale results after the user clears the search box.
            if (!append || !query) {
                results.value = [];
                totalLoaded.value = 0;
                hasMore.value = false;
                isPartial.value = false;
                lastSearchedQuery.value = "";
                isStale.value = false;
            }
            isSearching.value = false;
            return;
        }

        if (!append) {
            lastSearchedQuery.value = query;
            // Decide the route once per fresh search; loadMore reuses it.
            activeUseApi = shouldUseApiFts();
            inFlightFreshQuery = query;
        }

        // Track generation to discard stale results from superseded searches
        const generation = ++searchGeneration;

        isSearching.value = true;
        try {
            const { results: searchResults, usedLocal } = await runEngine(
                query,
                offset,
                activeUseApi,
                !append,
            );

            // Discard if a newer search was started while this one was running
            if (generation !== searchGeneration) return;

            if (append) {
                // Content can shift between page loads, so a later page may repeat a result.
                const shown = new Set(results.value.map((r) => r.docId));
                results.value = [
                    ...results.value,
                    ...searchResults.filter((r) => !shown.has(r.docId)),
                ];
            } else {
                results.value = searchResults;
            }
            totalLoaded.value = offset + searchResults.length;
            hasMore.value = searchResults.length === pageSize;
            source.value = usedLocal ? "local" : "api";
            // Local results are an incomplete view only when a sync cutoff is in effect.
            isPartial.value = usedLocal && cutoffSet();
            if (!append) isStale.value = false;
        } catch (e) {
            console.error("FTS search error:", e);
        } finally {
            // Only clear searching state if this is still the active search
            if (generation === searchGeneration) {
                isSearching.value = false;
            }
            if (!append && inFlightFreshQuery === query) {
                inFlightFreshQuery = null;
            }
        }
    }

    async function loadMore() {
        if (isSearching.value || !hasMore.value) return;
        await doSearch(currentQuery, totalLoaded.value, true);
    }

    async function refresh() {
        isStale.value = false;
        await doSearch(currentQuery, 0, false);
    }

    function runSearch() {
        // Drop any pending debounced run so an explicit runSearch (Enter, recent pick, modal
        // reopen) cannot double-fire alongside the query watcher.
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = undefined;
        }
        const q = queryRef.value.trim();
        // A same-query fresh search may already be in flight (the debounce fired just before
        // this explicit trigger, or a double Enter/Go-click) — skip the redundant network call.
        if (inFlightFreshQuery === q) return;
        currentQuery = q;
        doSearch(q, 0, false);
    }

    function cancel() {
        // Bumping the generation invalidates any in-flight doSearch so its results are discarded.
        searchGeneration++;
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = undefined;
        }
        // Reset dedup tracking so a subsequent runSearch() for the same query isn't skipped
        // just because the now-cancelled search hasn't settled yet.
        inFlightFreshQuery = null;
        isSearching.value = false;
    }

    function reset() {
        cancel();
        currentQuery = "";
        results.value = [];
        totalLoaded.value = 0;
        hasMore.value = false;
        lastSearchedQuery.value = "";
        isPartial.value = false;
        source.value = "local";
    }

    let stopQueryWatch: WatchStopHandle | null = null;

    function startQueryWatch() {
        if (stopQueryWatch) stopQueryWatch();
        stopQueryWatch = null;

        if (isTriggerOnly()) return;
        stopQueryWatch = watch(
            queryRef,
            (newQuery) => {
                isStale.value = false;
                if (debounceTimer) clearTimeout(debounceTimer);
                // Invalidate any in-flight search before the debounce, so a slow previous
                // doSearch can't publish stale results after the query has changed.
                searchGeneration++;
                currentQuery = newQuery;
                const d = getDebounce();
                debounceTimer = setTimeout(() => {
                    doSearch(newQuery, 0, false);
                }, typeof d === "number" ? d : 0);
            },
            { immediate: true },
        );
    }

    // Watch query with debounce (unless trigger-only mode). If debounce is reactive,
    // restart the watcher when the mode changes (e.g. desktop ↔ mobile).
    startQueryWatch();
    if (isRef(debounceMs)) {
        watch(debounceMs, () => startQueryWatch());
    }

    // Watch language changes — re-search immediately with current query
    if (options.languageId) {
        watch(options.languageId, () => {
            if (currentQuery) {
                doSearch(currentQuery, 0, false);
            }
        });
    }

    // Watch filter changes — re-search immediately with current query (deep: the ref
    // holds a plain object the caller may mutate field-by-field).
    if (options.filters) {
        watch(
            options.filters,
            () => {
                isStale.value = false;
                if (currentQuery) {
                    doSearch(currentQuery, 0, false);
                }
            },
            { deep: true },
        );
    }

    // Upgrade partial (offline/fallback) results when connectivity returns: re-run the
    // current search so it routes to the API and replaces the incomplete local view.
    watch(isConnected, (online) => {
        if (online && currentQuery && isPartial.value) {
            doSearch(currentQuery, 0, false);
        }
    });

    if (getCurrentScope()) {
        attachFtsLiveSync(
            results,
            {
                getId: (r) => r.docId,
                patch: (r, live) => ({ ...r, doc: live as ContentDto }),
            },
            {
                docType: DocType.Content,
                watchDexie: true,
                dexiePrune: () => source.value === "local",
                stale: isStale,
                query: queryRef,
                strictMatch: options.strictMatch,
            },
        );

        onScopeDispose(() => {
            if (debounceTimer) clearTimeout(debounceTimer);
            if (stopQueryWatch) stopQueryWatch();
        });
    }

    return {
        results,
        isSearching,
        loadMore,
        hasMore,
        totalLoaded,
        lastSearchedQuery,
        runSearch,
        cancel,
        reset,
        source,
        isPartial,
        isStale,
        refresh,
        markStale: () => markFtsStale(isStale),
    };
}
